import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import AbcIcon from '@mui/icons-material/Abc';
import { Box, FormControl, InputLabel, OutlinedInput, Typography } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, validateUniqueInList, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { createIMaskInput } from './Utils/createIMaskInput';
import { type } from './types';
const WidgetFormText = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const value = props.getDataString();
    const formLabelSuffix = getFormLabelSuffix(props);
    const elementLabel = formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label;
    const error = getVisibleError(props);
    const readOnly = props.config.readOnly || ((_a = props.rootParams.formParams) === null || _a === void 0 ? void 0 : _a.readOnly);
    const inputComponent = React.useMemo(() => {
        if (props.config.mask) {
            if (props.config.mask.type === 'pattern') {
                return createIMaskInput({
                    mask: props.config.mask.pattern,
                    definitions: props.config.mask.definitions &&
                        Object.fromEntries(Object.entries(props.config.mask.definitions).map(([key, value]) => [key, new RegExp(value)])),
                    overwrite: true,
                    lazy: !props.config.mask.placeholderChar,
                    placeholderChar: props.config.mask.placeholderChar,
                });
            }
            else {
                return createIMaskInput(props.config.mask.config);
            }
        }
        else {
            return undefined;
        }
    }, [props.config.mask]);
    let shrink = undefined;
    if ((((_b = props.config.mask) === null || _b === void 0 ? void 0 : _b.type) === 'pattern' && props.config.mask.placeholderChar) || ((_c = props.config.mask) === null || _c === void 0 ? void 0 : _c.type) === 'imask') {
        shrink = true;
    }
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.required, error: !!error, disabled: props.config.disabled },
        React.createElement(InputLabel, { htmlFor: props.id, variant: 'outlined', required: (_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.required, shrink: shrink, error: !!error, disabled: props.config.disabled }, elementLabel),
        React.createElement(FormTooltip, { tooltip: props.config.tooltip },
            React.createElement(OutlinedInput, { id: props.id, label: elementLabel, fullWidth: true, size: 'medium', required: (_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.required, notched: shrink, error: !!error, value: value, multiline: props.config.multiline, rows: props.config.multiline ? props.config.rows : undefined, disabled: props.config.disabled, readOnly: readOnly, onChange: (event) => {
                    if (readOnly) {
                        return;
                    }
                    let newValue = event.target.value;
                    if (props.config.emptyAsNull && !newValue) {
                        newValue = undefined;
                    }
                    props.setData(newValue);
                    renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                }, inputComponent: inputComponent, inputProps: {
                    style: { fontFamily: ((_g = props.config.style) === null || _g === void 0 ? void 0 : _g.monospace) ? 'monospace' : undefined },
                    tabIndex: props.editMode ? -1 : undefined,
                }, onBlur: inputComponent
                    ? undefined
                    : (event) => {
                        if (readOnly) {
                            return;
                        }
                        const inputValue = props.config.includeWhiteSpace ? event.target.value : event.target.value.trim();
                        if (inputValue !== value) {
                            let newValue = inputValue;
                            if (props.config.emptyAsNull && !newValue) {
                                newValue = undefined;
                            }
                            props.setData(newValue);
                            renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                        }
                    }, autoFocus: (_h = (!props.editMode && props.config.autoFocus)) !== null && _h !== void 0 ? _h : false, "aria-describedby": ariaDescribedBy })),
        props.config.showCharacterCount ? (React.createElement(Box, { sx: { display: 'flex' } },
            React.createElement(Box, { sx: { flexGrow: 1 } },
                React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })),
            React.createElement(Typography, { variant: 'caption', sx: { pt: 1, color: themeColors.text.subtle } },
                value === undefined ? 0 : 'Characters: ' + value.length,
                ((_j = props.config.validate) === null || _j === void 0 ? void 0 : _j.maxLength) != null ? '/' + ((_k = props.config.validate) === null || _k === void 0 ? void 0 : _k.maxLength) : ''))) : (React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description }))));
};
const WidgetFormTextEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Text: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            tooltip: true,
            disabled: true,
            readOnly: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.checkbox',
                    key: 'multiline',
                    label: 'Multiline',
                },
                {
                    type: 'form.number',
                    key: 'rows',
                    label: 'Rows',
                    hidden: { when: '^multiline', dataNotEq: true },
                    style: { width: 200 },
                },
                {
                    type: 'form.checkbox',
                    key: 'showCharacterCount',
                    label: 'Show Character Count',
                },
                {
                    type: 'form.checkbox',
                    key: 'emptyAsNull',
                    label: 'Empty As Null',
                },
                {
                    type: 'form.checkbox',
                    key: 'includeWhiteSpace',
                    defaultValue: false,
                    label: 'Include White Space',
                    tooltip: {
                        text: 'Allow spaces before and after text.',
                    },
                },
            ],
        }), buildValidateTab(props, {
            required: true,
            maxLength: true,
            uniqueInList: true,
            regex: true,
        }), buildHiddenTab(props), buildStyleTab(props, {
            children: [
                {
                    type: 'form.checkbox',
                    key: 'style.monospace',
                    label: 'Monospace Font',
                },
            ],
        }), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormText, Object.assign({}, props))));
};
export const widgetFormText = {
    type,
    init: (props) => {
        if (props.getData() == null && props.config.defaultValue != null) {
            props.setData(props.config.defaultValue);
        }
    },
    validate: (props) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataStringOrUndefined();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (!value) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        if (value) {
            let regex = undefined;
            if (typeof ((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.regex) === 'string') {
                let regexString = (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.regex;
                if (!(regexString === null || regexString === void 0 ? void 0 : regexString.startsWith('^'))) {
                    regexString = '^' + regexString;
                }
                if (!(regexString === null || regexString === void 0 ? void 0 : regexString.endsWith('$'))) {
                    regexString = regexString + '$';
                }
                try {
                    regex = new RegExp(regexString);
                }
                catch (error) {
                    console.error(props.config.label + ' invalid validation regex', error);
                    return props.config.label + ' invalid validation regex';
                }
            }
            else if (((_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.regex) instanceof RegExp) {
                regex = (_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.regex;
            }
            if (regex && !regex.test(value)) {
                return ((_h = (_g = props.config.validate) === null || _g === void 0 ? void 0 : _g.regexMessage) !== null && _h !== void 0 ? _h : props.config.label + ' does not match the pattern ' + ((_k = (_j = props.config.validate) === null || _j === void 0 ? void 0 : _j.regex) !== null && _k !== void 0 ? _k : ''.toString()));
            }
            if (((_l = props.config.validate) === null || _l === void 0 ? void 0 : _l.maxLength) != null && value.length > ((_m = props.config.validate) === null || _m === void 0 ? void 0 : _m.maxLength)) {
                return ((_o = props.config.validate.maxLengthMessage) !== null && _o !== void 0 ? _o : (props.config.label ? props.config.label + ' length cannot be greater than ' : 'Length cannot be greater than ') +
                    props.config.validate.maxLength);
            }
        }
        if (((_p = props.config.validate) === null || _p === void 0 ? void 0 : _p.uniqueInList) || ((_q = props.config.validate) === null || _q === void 0 ? void 0 : _q.uniqueInGrid)) {
            const error = validateUniqueInList(props, value, props.dataPath);
            if (error) {
                return (_r = props.config.validate.uniqueInListMessage) !== null && _r !== void 0 ? _r : error;
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), props.getDataStringOrUndefined());
    },
    component: WidgetFormText,
    icon: React.createElement(AbcIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormTextEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'text', label: 'Text' }),
};
